import {ClauseFragment, EquationFragment, Fragment, FragmentType, SectionFragment} from '@nh/angular-tses';
import {DASClauseType} from '../types/das-clause-type';
import {DasSectionType} from '../types/das-section-type';
import {DepartureFragment} from '../types/departure-fragment';

export const dasFragmentIndexer = (departure: DepartureFragment): Record<string, string> => {
  if (!departure || !departure.indexedDeparture) {
    return {};
  }

  const indexLookup: Record<string, string> = {};

  let currentClause: ClauseFragment;

  let sectionCount: number = 0;
  let headingCount: number = 0;
  let normalClauseCount: number = 0;
  let tableCounter: number = 0;
  let figureCounter: number = 0;
  let equationCounter: number = 0;

  const departureChildPredicate = (s: Fragment): boolean => {
    const section: SectionFragment = s as SectionFragment;

    const INDEXED_SECTION_TYPES: ReadonlySet<string> = new Set([
      DasSectionType.SUBMISSION_IN_PREPARATION,
      DasSectionType.SPECIALIST_COMMENTS
    ]);

    return INDEXED_SECTION_TYPES.has(section.sectionType);
  };

  const handleSectionFragment = (fragment: Fragment): void => {
    const section: SectionFragment = fragment as SectionFragment;

    sectionCount++;
    headingCount = normalClauseCount = 0;
    currentClause = null;
    indexLookup[section.id.value] = `${sectionCount}.`;
  };

  const handleClauseFragment = (fragment: Fragment): void => {
    currentClause = fragment as ClauseFragment;

    tableCounter = figureCounter = equationCounter = 0;

    switch (currentClause.clauseType) {
      case DASClauseType.HEADING:
        headingCount++;
        normalClauseCount = 0;
        indexLookup[currentClause.id.value] = `${sectionCount}.${headingCount}`;
        break;
      case DASClauseType.NORMAL:
        normalClauseCount++;
        indexLookup[currentClause.id.value] = `${sectionCount}.${headingCount}.${normalClauseCount}`;
        break;
    }
  };

  const getCaptionedSiblingCount = (fragment): number => {
    return currentClause.children.filter((f: Fragment) => {
      return f.is(fragment.type) && (!f.is(FragmentType.EQUATION) || !(f as EquationFragment).inline);
    }).length;
  };

  const getMultiCaptionedLabel = (counter: number): string => {
    let index: number = counter;
    let label: string = '';
    while (--index >= 0) {
      label += String.fromCharCode('a'.charCodeAt(0) + (index % 26));
      index /= 26;
    }

    return label;
  };

  const handleCaptionedFragment = (fragment: Fragment, counter: number, prefix: string): void => {
    const clauseSiblingCount: number = getCaptionedSiblingCount(fragment);
    const postfixLabel: string = clauseSiblingCount === 1 ? '' : getMultiCaptionedLabel(counter);

    switch (currentClause.clauseType) {
      case DASClauseType.HEADING:
        indexLookup[fragment.id.value] = `${prefix} ${sectionCount}.${headingCount}${postfixLabel}`;
        break;
      case DASClauseType.NORMAL:
        indexLookup[
          fragment.id.value
        ] = `${prefix} ${sectionCount}.${headingCount}.${normalClauseCount}${postfixLabel}`;
        break;
    }
  };

  const indexCallback = (fragment: Fragment): void => {
    if (fragment.is(FragmentType.SECTION)) {
      handleSectionFragment(fragment);
      iterateChildren(fragment);
    } else if (fragment.is(FragmentType.CLAUSE)) {
      handleClauseFragment(fragment);
      iterateChildren(fragment);
    } else if (fragment.is(FragmentType.TABLE)) {
      tableCounter++;
      handleCaptionedFragment(fragment, tableCounter, 'Table');
    } else if (fragment.is(FragmentType.FIGURE)) {
      figureCounter++;
      handleCaptionedFragment(fragment, figureCounter, 'Figure');
    } else if (fragment.is(FragmentType.EQUATION) && !(fragment as EquationFragment).inline) {
      equationCounter++;
      handleCaptionedFragment(fragment, equationCounter, 'Equation');
    }
  };

  const iterateChildren = (fragment: Fragment): void => {
    fragment.children.forEach(indexCallback);
  };

  departure.children.filter(departureChildPredicate).forEach(indexCallback);

  return indexLookup;
};
